import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  Pressable,
  StyleSheet,
  ScrollView,
  Platform,
  Modal,
  ActivityIndicator,
  Alert,
  useWindowDimensions,
} from 'react-native';
import { StatusBar } from 'expo-status-bar';
import { useFocusEffect } from '@react-navigation/native';
import { useTheme } from '../context/ThemeContext';
import HamburgerMenu from '../components/HamburgerMenu';
import ProfileIcon from '../components/ProfileIcon';
import Stopwatch from '../components/Stopwatch';
import MaterialIcons from '@expo/vector-icons/MaterialIcons';
import BrandedConfirmModal from '../components/BrandedConfirmModal';
import StudentNotesEditor from '../components/StudentNotesEditor';
import StudentNotesScrollableList from '../components/StudentNotesScrollableList';
import StudentNotesShareModal from '../components/StudentNotesShareModal';
import HandbookEntryCommentComposer from '../components/HandbookEntryCommentComposer';
import HandbookEntryCommentsList from '../components/HandbookEntryCommentsList';
import {
  fetchStudentNoteById,
  fetchMyNotes,
  fetchFavouriteNotes,
  fetchOtherFavouriteNotes,
  createStudentNote,
  updateStudentNote,
  deleteStudentNote,
  sendStudentNotification,
} from '../utils/studentNotesApi';
import {
  readFavouritesFromNote,
  isNoteStarredForUser,
  isOwnStudentNote,
} from '../utils/studentNotesFavourites';
import { resolveNoteTitle, firstLineFromBody } from '../utils/studentNotesMarkdown';
import { getApiUrl } from '../utils/apiConfig';
import {
  normalizeStudentUserRecord,
  isUserRecord,
} from '../utils/sessionUser';
import {
  readPermissionsFromNote,
  newlyAddedShareStudentIds,
} from '../utils/studentNotesPermissions';

function snapshotsEqual(a, b) {
  if (!a || !b) return false;
  return (
    String(a.id ?? '') === String(b.id ?? '') &&
    String(a.title ?? '') === String(b.title ?? '') &&
    String(a.body ?? '') === String(b.body ?? '')
  );
}

export default function StudentNotesPage({ navigation, route }) {
  const theme = useTheme();
  const { width: windowWidth } = useWindowDimensions();
  const stopwatchRef = useRef(null);
  const styles = getStyles(theme);
  const [currentUser, setCurrentUser] = useState(route?.params?.user ?? null);
  const [leftPanelMinimized, setLeftPanelMinimized] = useState(false);
  const leftMenuWidth = leftPanelMinimized
    ? 44
    : windowWidth < 640
      ? Math.max(100, Math.round(windowWidth * 0.38))
      : 260;
  const [myNotes, setMyNotes] = useState([]);
  const [favouriteNotes, setFavouriteNotes] = useState([]);
  const [otherFavouriteNotes, setOtherFavouriteNotes] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [favouritesLoading, setFavouritesLoading] = useState(true);
  const [otherFavouritesLoading, setOtherFavouritesLoading] = useState(true);
  const [activeIsFavourite, setActiveIsFavourite] = useState(false);
  const [activeFavouritedBy, setActiveFavouritedBy] = useState([]);
  const [favouriteSaving, setFavouriteSaving] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [saving, setSaving] = useState(false);
  const [snapshot, setSnapshot] = useState({ id: null, title: '', body: '' });
  const [menuNote, setMenuNote] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [pendingLoad, setPendingLoad] = useState(null);
  const [readPermission, setReadPermission] = useState([]);
  const [commentPermission, setCommentPermission] = useState([]);
  const [editPermission, setEditPermission] = useState([]);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareStudents, setShareStudents] = useState([]);
  const [loadingShareStudents, setLoadingShareStudents] = useState(false);
  const [shareSaving, setShareSaving] = useState(false);
  const [commentsRefreshKey, setCommentsRefreshKey] = useState(0);
  const [noteAuthorId, setNoteAuthorId] = useState(null);

  const userId = currentUser?.id ?? route?.params?.userId;

  const isDirty = !snapshotsEqual(
    { id: activeId, title, body },
    snapshot
  );

  const loadLists = useCallback(async () => {
    if (userId == null) return;
    setListLoading(true);
    setFavouritesLoading(true);
    setOtherFavouritesLoading(true);
    try {
      const [mine, favs, otherFavs] = await Promise.all([
        fetchMyNotes(userId),
        fetchFavouriteNotes(userId),
        fetchOtherFavouriteNotes(userId),
      ]);
      setMyNotes(mine);
      setFavouriteNotes(favs);
      setOtherFavouriteNotes(otherFavs);
    } catch (e) {
      console.error('StudentNotes list:', e);
      setMyNotes([]);
      setFavouriteNotes([]);
      setOtherFavouriteNotes([]);
    } finally {
      setListLoading(false);
      setFavouritesLoading(false);
      setOtherFavouritesLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (route?.params?.user && isUserRecord(route.params.user)) {
      setCurrentUser(normalizeStudentUserRecord(route.params.user));
      return;
    }
    const uid = route?.params?.userId;
    if (uid == null) return;
    fetch(`${getApiUrl()}/api/users/${uid}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (isUserRecord(data)) setCurrentUser(normalizeStudentUserRecord(data));
      })
      .catch(() => {});
  }, [route?.params?.user, route?.params?.userId]);

  useFocusEffect(
    useCallback(() => {
      loadLists();
    }, [loadLists])
  );

  const applyEditorState = useCallback((note) => {
    if (!note) {
      setActiveId(null);
      setTitle('');
      setBody('');
      setReadPermission([]);
      setCommentPermission([]);
      setEditPermission([]);
      setNoteAuthorId(null);
      setActiveIsFavourite(false);
      setActiveFavouritedBy([]);
      setSnapshot({ id: null, title: '', body: '' });
      return;
    }
    const t = note.title != null ? String(note.title) : '';
    const b = note.body != null ? String(note.body) : '';
    const perms = readPermissionsFromNote(note);
    setActiveId(note.id);
    setTitle(t);
    setBody(b);
    setReadPermission(perms.readPermission);
    setCommentPermission(perms.commentPermission);
    setEditPermission(perms.editPermission);
    setNoteAuthorId(note.created_by ?? note.createdBy ?? null);
    const fav = readFavouritesFromNote(note);
    setActiveIsFavourite(fav.isFavourite);
    setActiveFavouritedBy(fav.favouritedBy);
    setSnapshot({ id: note.id, title: t, body: b });
  }, []);

  const activeNoteForStar = {
    id: activeId,
    created_by: noteAuthorId,
    createdBy: noteAuthorId,
    isFavourite: activeIsFavourite,
    favouritedBy: activeFavouritedBy,
  };

  const isStarred = isNoteStarredForUser(activeNoteForStar, userId);

  const handleToggleFavourite = useCallback(async () => {
    if (userId == null) return;
    if (activeId == null) {
      Alert.alert('Save first', 'Save this note before adding it to favourites.');
      return;
    }
    const isOwn = isOwnStudentNote(activeNoteForStar, userId);
    if (isOwn && isDirty) {
      Alert.alert('Save first', 'Save your changes before updating favourites.');
      return;
    }
    setFavouriteSaving(true);
    try {
      let saved;
      if (isOwn) {
        saved = await updateStudentNote(activeId, { isFavourite: !activeIsFavourite });
      } else {
        const uid = Number(userId);
        const next = activeFavouritedBy.includes(uid)
          ? activeFavouritedBy.filter((id) => id !== uid)
          : [...activeFavouritedBy, uid];
        saved = await updateStudentNote(activeId, { favouritedBy: next });
      }
      const fav = readFavouritesFromNote(saved);
      setActiveIsFavourite(fav.isFavourite);
      setActiveFavouritedBy(fav.favouritedBy);
      await loadLists();
    } catch (e) {
      Alert.alert('Favourite failed', e.message || 'Could not update favourite.');
    } finally {
      setFavouriteSaving(false);
    }
  }, [
    userId,
    activeId,
    isDirty,
    activeIsFavourite,
    activeFavouritedBy,
    noteAuthorId,
    loadLists,
  ]);

  useEffect(() => {
    const rawNoteId = route?.params?.noteId;
    if (rawNoteId == null || userId == null) return;
    const noteId = parseInt(String(rawNoteId), 10);
    if (!Number.isFinite(noteId)) return;
    if (activeId === noteId) return;
    let cancelled = false;
    (async () => {
      try {
        const note = await fetchStudentNoteById(noteId, { viewerStudentId: userId });
        if (!cancelled && note) applyEditorState(note);
      } catch (e) {
        console.warn('StudentNotes deep link load:', e?.message || e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [route?.params?.noteId, userId, activeId, applyEditorState]);

  const canCommentOnNote =
    activeId != null &&
    userId != null &&
    (Number(noteAuthorId) === Number(userId) ||
      commentPermission.map(Number).includes(Number(userId)));

  const fetchShareStudents = useCallback(async () => {
    setLoadingShareStudents(true);
    try {
      const res = await fetch(
        `${getApiUrl()}/api/users?visible=true&requester_id=${encodeURIComponent(String(userId))}`
      );
      if (!res.ok) {
        setShareStudents([]);
        return;
      }
      const data = await res.json();
      const list = Array.isArray(data) ? data : [];
      const selfId = Number(userId);
      setShareStudents(
        list.filter((s) => {
          if (!Number.isFinite(selfId)) return true;
          return Number(s.id) !== selfId;
        })
      );
    } catch (e) {
      console.error('StudentNotes share students:', e);
      setShareStudents([]);
    } finally {
      setLoadingShareStudents(false);
    }
  }, [userId]);

  const handleSharePress = useCallback(() => {
    setShareOpen(true);
    fetchShareStudents();
  }, [fetchShareStudents]);

  const noteHasContent =
    String(title || '').trim() !== '' || String(body || '').trim() !== '';

  const handleShareSave = useCallback(
    async (draft) => {
      if (userId == null) return;
      setShareSaving(true);
      const previous = {
        readPermission,
        commentPermission,
        editPermission,
      };
      const newlyAdded = newlyAddedShareStudentIds(previous, draft);
      try {
        let saved;
        if (activeId == null) {
          const resolvedTitle = resolveNoteTitle(title, body);
          const titleToSend =
            String(title || '').trim() !== ''
              ? String(title).trim()
              : firstLineFromBody(body) || resolvedTitle;
          if (!titleToSend && !String(body || '').trim()) {
            Alert.alert(
              'Nothing to save',
              'Add a title or note body before saving sharing settings.'
            );
            return;
          }
          saved = await createStudentNote({
            createdBy: userId,
            title: titleToSend,
            body,
            readPermission: draft.readPermission,
            commentPermission: draft.commentPermission,
            editPermission: draft.editPermission,
          });
        } else {
          saved = await updateStudentNote(activeId, {
            readPermission: draft.readPermission,
            commentPermission: draft.commentPermission,
            editPermission: draft.editPermission,
          });
        }
        const perms = readPermissionsFromNote(saved);
        const t = saved.title != null ? String(saved.title) : title;
        const b = saved.body != null ? String(saved.body) : body;
        setActiveId(saved.id);
        setTitle(t);
        setBody(b);
        setReadPermission(perms.readPermission);
        setCommentPermission(perms.commentPermission);
        setEditPermission(perms.editPermission);
        const fav = readFavouritesFromNote(saved);
        setActiveIsFavourite(fav.isFavourite);
        setActiveFavouritedBy(fav.favouritedBy);
        setSnapshot({ id: saved.id, title: t, body: b });
        await loadLists();

        if (newlyAdded.length > 0) {
          const senderName =
            currentUser?.student_name ||
            currentUser?.username ||
            'A student';
          const noteLabel = resolveNoteTitle(title, body) || 'a note';
          const messageText = `${senderName} shared "${noteLabel}" with you. Open Student Notes to view it.`;
          await Promise.allSettled(
            newlyAdded.map((recipientId) =>
              sendStudentNotification({
                userId: recipientId,
                text: messageText,
                link: 'StudentNotes',
                createdBy: userId,
              })
            )
          );
        }

        setShareOpen(false);
      } catch (e) {
        Alert.alert('Share failed', e.message || 'Could not update permissions.');
      } finally {
        setShareSaving(false);
      }
    },
    [
      activeId,
      userId,
      readPermission,
      commentPermission,
      editPermission,
      currentUser,
      title,
      body,
      loadLists,
    ]
  );

  const requestLoadNote = useCallback(
    (note) => {
      if (!note) return;
      if (isDirty) {
        setPendingLoad(note);
        setConfirm({
          kind: 'unsaved',
          title: 'Unsaved changes',
          message: 'Save your current note before opening another?',
          confirmLabel: 'Save',
          cancelLabel: 'Discard',
          thirdLabel: 'Cancel',
        });
        return;
      }
      applyEditorState(note);
    },
    [isDirty, applyEditorState]
  );

  const performSave = useCallback(async () => {
    if (userId == null) {
      Alert.alert('Not signed in', 'Log in to save notes.');
      return false;
    }
    const resolvedTitle = resolveNoteTitle(title, body);
    const titleToSend =
      String(title || '').trim() !== '' ? String(title).trim() : firstLineFromBody(body) || resolvedTitle;

    setSaving(true);
    try {
      let saved;
      if (activeId != null) {
        saved = await updateStudentNote(activeId, { title: titleToSend, body });
      } else {
        saved = await createStudentNote({
          createdBy: userId,
          title: titleToSend,
          body,
        });
      }
      const t = saved.title != null ? String(saved.title) : titleToSend;
      const b = saved.body != null ? String(saved.body) : body;
      const perms = readPermissionsFromNote(saved);
      setActiveId(saved.id);
      setTitle(t);
      setBody(b);
      setReadPermission(perms.readPermission);
      setCommentPermission(perms.commentPermission);
      setEditPermission(perms.editPermission);
      const fav = readFavouritesFromNote(saved);
      setActiveIsFavourite(fav.isFavourite);
      setActiveFavouritedBy(fav.favouritedBy);
      setSnapshot({ id: saved.id, title: t, body: b });
      await loadLists();
      return true;
    } catch (e) {
      Alert.alert('Save failed', e.message || 'Could not save note.');
      return false;
    } finally {
      setSaving(false);
    }
  }, [userId, title, body, activeId, loadLists]);

  const handleSavePress = () => {
    performSave();
  };

  const handleDeleteNote = (note) => {
    setConfirm({
      kind: 'delete',
      title: 'Delete note?',
      message: `Delete "${resolveNoteTitle(note.title, note.body)}"?`,
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
      destructive: true,
      noteId: note.id,
    });
  };

  const closeConfirm = () => {
    setConfirm(null);
    setPendingLoad(null);
  };

  const onConfirmAction = async () => {
    const c = confirm;
    if (!c) return;
    if (c.kind === 'delete' && c.noteId != null) {
      try {
        await deleteStudentNote(c.noteId);
        if (activeId === c.noteId) applyEditorState(null);
        await loadLists();
      } catch (e) {
        Alert.alert('Delete failed', e.message || 'Could not delete.');
      }
      closeConfirm();
      return;
    }
    if (c.kind === 'unsaved') {
      const ok = await performSave();
      if (!ok) return;
      if (pendingLoad) applyEditorState(pendingLoad);
      else applyEditorState(null);
      closeConfirm();
      return;
    }
    closeConfirm();
  };

  const onCancelAction = async () => {
    const c = confirm;
    if (c?.kind === 'unsaved') {
      if (pendingLoad) applyEditorState(pendingLoad);
      else applyEditorState(null);
      closeConfirm();
      return;
    }
    closeConfirm();
  };

  const NOTE_MENU_WIDTH = 168;

  const closeNoteMenu = useCallback(() => {
    setMenuNote(null);
    setMenuAnchor(null);
  }, []);

  const openNoteMenu = useCallback((note, anchor) => {
    setMenuNote(note);
    setMenuAnchor(anchor);
  }, []);

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return undefined;
    if (!menuNote && !shareOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      if (menuNote) closeNoteMenu();
      if (shareOpen && !shareSaving) setShareOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [menuNote, shareOpen, shareSaving, closeNoteMenu]);

  const closeShareModal = useCallback(() => {
    if (!shareSaving) setShareOpen(false);
  }, [shareSaving]);

  const menuTop = menuAnchor ? menuAnchor.y + menuAnchor.height + 4 : 140;
  const menuLeft = menuAnchor
    ? Math.max(12, menuAnchor.x + menuAnchor.width - NOTE_MENU_WIDTH)
    : 12;

  const noteMenuLayer =
    menuNote != null ? (
      <View
        style={styles.noteMenuScreenOverlay}
        pointerEvents="box-none"
        nativeID="student-notes-menu-overlay"
        {...(Platform.OS === 'web' ? { dataSet: { studentNotesMenuOverlay: 'true' } } : {})}
      >
        <Pressable
          style={styles.noteMenuDismissLayer}
          onPress={closeNoteMenu}
          accessibilityRole="button"
          accessibilityLabel="Close menu"
          nativeID="student-notes-menu-dismiss"
        />
        <View
          style={[
            styles.noteMenuPopup,
            { top: menuTop, left: menuLeft, width: NOTE_MENU_WIDTH },
          ]}
          nativeID="student-notes-note-menu-popup"
          {...(Platform.OS === 'web' ? { dataSet: { studentNotesNoteMenu: 'true' } } : {})}
        >
          <Pressable
            style={styles.noteMenuItem}
            onPress={() => {
              closeNoteMenu();
              requestLoadNote(menuNote);
            }}
          >
            <Text style={styles.noteMenuItemText}>Edit</Text>
          </Pressable>
          <Pressable
            style={[styles.noteMenuItem, styles.noteMenuItemLast]}
            onPress={() => {
              closeNoteMenu();
              handleDeleteNote(menuNote);
            }}
          >
            <Text style={[styles.noteMenuItemText, styles.noteMenuItemDangerText]}>Delete</Text>
          </Pressable>
        </View>
      </View>
    ) : null;

  const showUnsavedThird = confirm?.kind === 'unsaved';

  return (
    <View
      style={styles.container}
      {...(Platform.OS === 'web'
        ? { dataSet: { screen: 'student-notes', theme: theme.isDarkMode ? 'dark' : 'light' } }
        : {})}
    >
      <StatusBar style={theme.isDarkMode ? 'light' : 'dark'} />
      <View style={styles.header}>
        <View style={styles.hamburgerContainer}>
          <HamburgerMenu
            navigation={navigation}
            stopwatchRef={stopwatchRef}
            currentUser={currentUser}
          />
        </View>
        <View style={styles.breadcrumbContainer}>
          <Text style={styles.breadcrumb}>Student Notes</Text>
        </View>
        <View style={styles.profileIconContainer}>
          <ProfileIcon navigation={navigation} user={currentUser} />
        </View>
      </View>

      <View style={styles.content}>
        <View
          style={[styles.leftMenu, { width: leftMenuWidth }]}
          {...(Platform.OS === 'web' ? { dataSet: { studentNotesLeftPane: 'true' } } : {})}
        >
          <TouchableOpacity
            style={styles.minimizeButton}
            onPress={() => setLeftPanelMinimized(!leftPanelMinimized)}
            activeOpacity={0.7}
          >
            <Text style={styles.minimizeButtonText}>{leftPanelMinimized ? '▶' : '◀'}</Text>
          </TouchableOpacity>
          {!leftPanelMinimized && (
            <>
              <TouchableOpacity
                style={styles.newNoteBtn}
                onPress={() => {
                  if (isDirty) {
                    setPendingLoad(null);
                    setConfirm({
                      kind: 'unsaved',
                      title: 'Unsaved changes',
                      message: 'Save your current note before starting a new one?',
                      confirmLabel: 'Save',
                      cancelLabel: 'Discard',
                    });
                    return;
                  }
                  applyEditorState(null);
                }}
              >
                <Text style={styles.newNoteBtnText}>+ New note</Text>
              </TouchableOpacity>
              <Text style={styles.listHeading}>My Notes</Text>
              <StudentNotesScrollableList
                notes={myNotes}
                loading={listLoading}
                emptyLabel="No saved notes yet"
                activeId={activeId}
                onPressNote={requestLoadNote}
                onDeletePress={handleDeleteNote}
                onMenuPress={openNoteMenu}
              />
              <Text style={[styles.listHeading, styles.listHeadingSpaced]}>Favourites</Text>
              <StudentNotesScrollableList
                notes={favouriteNotes}
                loading={favouritesLoading}
                emptyLabel="No favourites yet"
                activeId={activeId}
                onPressNote={requestLoadNote}
                onDeletePress={handleDeleteNote}
                onMenuPress={openNoteMenu}
              />
              <Text style={[styles.listHeading, styles.listHeadingSpaced]}>Other Favourites</Text>
              <StudentNotesScrollableList
                notes={otherFavouriteNotes}
                loading={otherFavouritesLoading}
                emptyLabel="No other favourites yet"
                activeId={activeId}
                onPressNote={requestLoadNote}
                onDeletePress={handleDeleteNote}
                onMenuPress={openNoteMenu}
              />
            </>
          )}
        </View>

        <View
          style={styles.editorPane}
          nativeID="student-notes-editor-pane"
          {...(Platform.OS === 'web' ? { dataSet: { studentNotesEditorPane: 'true' } } : {})}
        >
          <ScrollView
            style={styles.editorScroll}
            contentContainerStyle={styles.editorScrollContent}
            showsVerticalScrollIndicator
            keyboardShouldPersistTaps="handled"
          >
          <View style={styles.titleToolbarRow}>
            {activeId != null ? (
              <TouchableOpacity
                style={styles.starButton}
                onPress={handleToggleFavourite}
                disabled={favouriteSaving}
                accessibilityRole="button"
                accessibilityLabel={isStarred ? 'Remove from favourites' : 'Add to favourites'}
                nativeID="student-notes-favourite-btn"
                {...(Platform.OS === 'web' ? { dataSet: { studentNotesFavouriteBtn: 'true' } } : {})}
              >
                {favouriteSaving ? (
                  <ActivityIndicator size="small" color={theme.colors.primary || '#d4af37'} />
                ) : (
                  <MaterialIcons
                    name={isStarred ? 'star' : 'star-border'}
                    size={28}
                    color={theme.colors.primary || '#d4af37'}
                  />
                )}
              </TouchableOpacity>
            ) : null}
            <TouchableOpacity
              style={styles.shareButton}
              onPress={handleSharePress}
              accessibilityRole="button"
              accessibilityLabel="Share note with students"
              nativeID="student-notes-share-btn"
              {...(Platform.OS === 'web' ? { dataSet: { studentNotesShareBtn: 'true' } } : {})}
            >
              <View style={styles.shareIconRow}>
                <MaterialIcons
                  name="person"
                  size={24}
                  color={theme.colors.primary || '#d4af37'}
                />
                <MaterialIcons
                  name="add"
                  size={20}
                  color={theme.colors.primary || '#d4af37'}
                  style={styles.shareIconPlus}
                />
              </View>
            </TouchableOpacity>
          </View>
          <TextInput
            nativeID="student-notes-title-input"
            style={styles.titleInput}
            value={title}
            onChangeText={setTitle}
            placeholder="Title (first line of body used if empty on save)"
            placeholderTextColor={theme.isDarkMode ? '#777' : '#999'}
          />
          <StudentNotesEditor body={body} onBodyChange={setBody} disabled={saving} />
          <View
            style={styles.buttonRow}
            nativeID="student-notes-button-row"
            {...(Platform.OS === 'web' ? { dataSet: { studentNotesButtonRow: 'true' } } : {})}
          >
            <TouchableOpacity
              onPress={handleSavePress}
              disabled={saving}
              activeOpacity={0.85}
              style={styles.saveTouchable}
            >
              <View
                style={[styles.button, styles.saveButton]}
                nativeID="student-notes-save-btn"
                {...(Platform.OS === 'web' ? { dataSet: { studentNotesSaveBtn: 'true' } } : {})}
              >
                {saving ? (
                  <ActivityIndicator color="#000" />
                ) : (
                  <Text style={styles.saveButtonText}>Save</Text>
                )}
              </View>
            </TouchableOpacity>
          </View>

          {activeId != null ? (
            <>
              <HandbookEntryCommentComposer
                studentNoteId={activeId}
                noteTitle={resolveNoteTitle(title, body)}
                user={currentUser}
                disabled={!canCommentOnNote || saving}
                onSuccess={() => setCommentsRefreshKey((k) => k + 1)}
              />
              <HandbookEntryCommentsList
                studentNoteId={activeId}
                noteTitle={resolveNoteTitle(title, body)}
                user={currentUser}
                refreshKey={commentsRefreshKey}
                focusCommentId={route?.params?.commentId}
                onViewRecorded={() => setCommentsRefreshKey((k) => k + 1)}
                onCommentsMutated={() => setCommentsRefreshKey((k) => k + 1)}
              />
            </>
          ) : null}
          </ScrollView>
        </View>
      </View>

      <BrandedConfirmModal
        visible={!!confirm && !showUnsavedThird}
        title={confirm?.title}
        message={confirm?.message}
        confirmLabel={confirm?.confirmLabel}
        cancelLabel={confirm?.cancelLabel}
        destructive={confirm?.destructive}
        onConfirm={onConfirmAction}
        onCancel={onCancelAction}
      />

      {showUnsavedThird && confirm ? (
        <Modal visible transparent animationType="fade" onRequestClose={closeConfirm}>
          <TouchableOpacity style={styles.menuBackdrop} activeOpacity={1} onPress={closeConfirm}>
            <View
              style={styles.unsavedSheet}
              {...(Platform.OS === 'web' ? { dataSet: { brandedConfirmModal: 'true' } } : {})}
            >
              <Text style={styles.unsavedTitle}>{confirm.title}</Text>
              <Text style={styles.unsavedMessage}>{confirm.message}</Text>
              <TouchableOpacity style={[styles.unsavedBtn, styles.saveButton]} onPress={onConfirmAction}>
                <Text style={styles.saveButtonText}>Save</Text>
              </TouchableOpacity>
              <TouchableOpacity style={[styles.unsavedBtn, styles.clearButton]} onPress={onCancelAction}>
                <Text style={styles.clearButtonText}>Discard</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.unsavedBtnPlain} onPress={closeConfirm}>
                <Text style={styles.unsavedCancelText}>Cancel</Text>
              </TouchableOpacity>
            </View>
          </TouchableOpacity>
        </Modal>
      ) : null}

      {noteMenuLayer}

      <StudentNotesShareModal
        visible={shareOpen}
        onClose={closeShareModal}
        noteSaved={activeId != null}
        noteHasContent={noteHasContent}
        students={shareStudents}
        loadingStudents={loadingShareStudents}
        readPermission={readPermission}
        commentPermission={commentPermission}
        editPermission={editPermission}
        onSave={handleShareSave}
        saving={shareSaving}
      />

      <Stopwatch ref={stopwatchRef} />
    </View>
  );
}

function getStyles(theme) {
  const gold = theme.colors.primary || '#d4af37';
  return StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: theme.isDarkMode ? theme.colors.background : '#faf8f4',
      ...(Platform.OS === 'web' ? { position: 'relative' } : {}),
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingTop: 50,
      paddingBottom: 16,
      paddingHorizontal: 20,
      borderBottomWidth: 1,
      borderBottomColor: theme.isDarkMode ? '#333' : '#EEE',
      backgroundColor: theme.isDarkMode ? theme.colors.background : '#FFFFFF',
    },
    hamburgerContainer: { marginRight: 15 },
    breadcrumbContainer: { flex: 1 },
    breadcrumb: {
      fontSize: 18,
      fontWeight: '600',
      color: theme.colors.heading,
    },
    profileIconContainer: {},
    content: {
      flex: 1,
      flexDirection: 'row',
      minHeight: 0,
      minWidth: 0,
      overflow: 'hidden',
    },
    leftMenu: {
      flexShrink: 0,
      borderRightWidth: 1,
      borderRightColor: theme.isDarkMode ? '#333' : '#DDD',
      backgroundColor: theme.isDarkMode ? theme.colors.surface : '#F5F5F5',
      paddingTop: 8,
    },
    minimizeButton: {
      alignSelf: 'flex-end',
      padding: 8,
      marginRight: 4,
    },
    minimizeButtonText: {
      fontSize: 16,
      color: gold,
      fontWeight: '700',
    },
    newNoteBtn: {
      marginHorizontal: 10,
      marginBottom: 10,
      paddingVertical: 10,
      borderRadius: 8,
      backgroundColor: gold,
      alignItems: 'center',
    },
    newNoteBtnText: {
      fontWeight: '700',
      fontSize: 14,
      color: '#000',
    },
    listHeading: {
      fontSize: 12,
      fontWeight: '600',
      color: theme.isDarkMode ? '#aaa' : '#666',
      marginHorizontal: 12,
      marginBottom: 6,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
    },
    listHeadingSpaced: {
      marginTop: 16,
    },
    editorPane: {
      flex: 1,
      minWidth: 0,
      overflow: 'hidden',
      backgroundColor: theme.isDarkMode ? '#1a1a1a' : '#faf8f4',
    },
    editorScroll: {
      flex: 1,
    },
    editorScrollContent: {
      flexGrow: 1,
      padding: 16,
      maxWidth: '100%',
      alignSelf: 'stretch',
    },
    titleToolbarRow: {
      flexDirection: 'row',
      justifyContent: 'flex-end',
      alignItems: 'center',
      gap: 4,
      marginBottom: 4,
      backgroundColor: 'transparent',
    },
    starButton: {
      padding: 8,
      borderRadius: 8,
      backgroundColor: 'transparent',
      minWidth: 44,
      alignItems: 'center',
      justifyContent: 'center',
    },
    shareButton: {
      padding: 8,
      borderRadius: 8,
      backgroundColor: 'transparent',
    },
    shareIconRow: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: 'transparent',
    },
    shareIconPlus: {
      marginLeft: 1,
      marginTop: 2,
    },
    titleInput: {
      fontSize: 22,
      fontWeight: '700',
      color: theme.colors.heading,
      borderBottomWidth: 1,
      borderBottomColor: theme.isDarkMode ? '#444' : '#DDD',
      paddingVertical: 10,
      marginBottom: 8,
      width: '100%',
      maxWidth: '100%',
      ...(Platform.OS === 'web' ? { outlineStyle: 'none', boxSizing: 'border-box' } : {}),
    },
    buttonRow: {
      flexDirection: 'row',
      justifyContent: 'center',
      gap: 12,
      marginTop: 16,
      paddingBottom: 12,
      backgroundColor: 'transparent',
      width: '100%',
      maxWidth: '100%',
      alignSelf: 'stretch',
    },
    button: {
      width: '100%',
      maxWidth: 280,
      paddingVertical: 14,
      paddingHorizontal: 24,
      borderRadius: 8,
      alignItems: 'center',
    },
    saveTouchable: {
      width: '100%',
      maxWidth: 280,
      alignSelf: 'center',
    },
    clearButton: {
      backgroundColor: theme.isDarkMode ? '#444' : '#E0E0E0',
    },
    clearButtonText: {
      fontSize: 16,
      fontWeight: '600',
      color: theme.colors.text,
    },
    saveButton: {
      backgroundColor: gold,
    },
    saveButtonText: {
      fontSize: 16,
      fontWeight: '700',
      color: '#000',
    },
    noteMenuScreenOverlay: {
      ...(Platform.OS === 'web'
        ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }
        : StyleSheet.absoluteFillObject),
      zIndex: 2000,
      backgroundColor: 'transparent',
    },
    noteMenuDismissLayer: {
      ...StyleSheet.absoluteFillObject,
      backgroundColor: 'transparent',
    },
    noteMenuPopup: {
      position: 'absolute',
      backgroundColor: theme.isDarkMode ? '#1e1e1e' : '#FFFFFF',
      borderRadius: 10,
      borderWidth: 2,
      borderColor: gold,
      overflow: 'hidden',
      zIndex: 2001,
      ...(Platform.OS === 'web'
        ? {
            boxShadow: theme.isDarkMode
              ? '0 8px 24px rgba(0,0,0,0.45)'
              : '0 8px 24px rgba(0,0,0,0.15)',
          }
        : {
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 4 },
            shadowOpacity: 0.25,
            shadowRadius: 8,
            elevation: 8,
          }),
    },
    noteMenuItem: {
      paddingVertical: 14,
      paddingHorizontal: 18,
      borderBottomWidth: 1,
      borderBottomColor: theme.isDarkMode ? '#333' : '#EEE',
      backgroundColor: theme.isDarkMode ? '#1e1e1e' : '#FFFFFF',
    },
    noteMenuItemLast: {
      borderBottomWidth: 0,
    },
    noteMenuItemText: {
      fontSize: 16,
      color: theme.colors.text,
    },
    noteMenuItemDangerText: {
      color: '#c62828',
      fontWeight: '600',
    },
    unsavedSheet: {
      width: '90%',
      maxWidth: 400,
      backgroundColor: theme.isDarkMode ? '#1e1e1e' : '#FFF',
      borderRadius: 12,
      padding: 22,
      borderWidth: 2,
      borderColor: gold,
    },
    unsavedTitle: {
      fontSize: 18,
      fontWeight: '700',
      color: theme.colors.heading,
      textAlign: 'center',
      marginBottom: 8,
    },
    unsavedMessage: {
      fontSize: 15,
      color: theme.colors.text,
      textAlign: 'center',
      marginBottom: 16,
    },
    unsavedBtn: {
      marginBottom: 10,
      borderRadius: 8,
      paddingVertical: 12,
      alignItems: 'center',
    },
    unsavedBtnPlain: {
      paddingVertical: 10,
      alignItems: 'center',
    },
    unsavedCancelText: {
      fontSize: 15,
      color: theme.isDarkMode ? '#aaa' : '#666',
    },
  });
}
